if (!customElements.get('madara-carousel')) {
  customElements.define(
    'madara-carousel',
    class MadaraCarousel extends HTMLElement {
      connectedCallback() {
        this.slider = this.querySelector('.swiper');
        this.track = this.querySelector('.swiper-wrapper');

        if (!this.slider || !this.track || !window.FoxTheme?.Carousel) {
          // Consumers wait on `ready` before they touch the slides, so settle it
          // here too rather than leaving them stalled on a carousel-less row.
          this.unavailable = true;
          this._markReady?.(null);
          this.reveal();
          return;
        }

        // Sections hand over pre-rendered items, so the slide class is applied
        // here instead of every caller having to know Swiper's markup contract.
        this.slides.forEach((slide) => slide.classList.add('swiper-slide'));

        // Carousels can be rendered inside a hidden panel (product tabs), where
        // Swiper would measure a width of 0. Wait until the element has a size.
        if (this.offsetWidth > 0) {
          this.init();
        } else {
          this.resizeObserver = new ResizeObserver(() => {
            if (this.offsetWidth === 0) return;
            this.init();
          });
          this.resizeObserver.observe(this);
        }
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        this.carousel?.slider?.destroy();
        this.carousel = null;
      }

      get slides() {
        return [...this.track.children];
      }

      get swiper() {
        return this.carousel?.slider;
      }

      /** Resolves with the Swiper instance, or with null if there is not one. */
      get ready() {
        if (!this._ready) {
          this._ready = new Promise((resolve) => {
            this._markReady = resolve;
          });

          if (this.swiper || this.unavailable) this._markReady(this.swiper ?? null);
        }

        return this._ready;
      }

      get loop() {
        return this.hasAttribute('loop');
      }

      get options() {
        const gap = Number(this.dataset.gap ?? 10);
        const gapDesktop = Number(this.dataset.gapDesktop ?? gap);

        return {
          loop: this.loop,
          // Swiper derives the number of clones from slidesPerView, which it
          // cannot do while that is 'auto', so duplicate the whole set instead.
          loopedSlides: this.loop ? this.slides.length : null,
          slidesPerView: 'auto',
          spaceBetween: gap,
          breakpoints: {
            768: { spaceBetween: gapDesktop },
          },
          // Nothing to scroll → Swiper locks itself and hides the navigation
          // instead of letting a row that already fits drift around.
          watchOverflow: true,
          centerInsufficientSlides: true,
          centeredSlides: this.hasAttribute('centered-slides'),
          slideToClickedSlide: this.hasAttribute('slide-to-clicked-slide'),
          threshold: 2,
          grabCursor: true,
          // Panels and slide contents can change size after init (tabs,
          // lazy-loaded media, theme editor), so keep measurements in sync.
          observer: true,
          observeParents: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
        };
      }

      init() {
        if (this.carousel) return;

        this.resizeObserver?.disconnect();
        this.resizeObserver = null;

        this.carousel = new FoxTheme.Carousel(this.slider, this.options);
        this.carousel.init();

        this.reveal();

        this._markReady?.(this.swiper);
        this.dispatchEvent(
          new CustomEvent('carousel:init', { bubbles: true, detail: { swiper: this.swiper } })
        );

        if (this.blockSelectBound) return;
        this.blockSelectBound = true;

        this.addEventListener('shopify:block:select', (event) => {
          const slide = event.target.closest('.swiper-slide');
          if (!slide) return;

          // Cloned slides shift the DOM order, so target the original index.
          const index = Number(slide.dataset.swiperSlideIndex ?? this.slides.indexOf(slide));
          if (this.loop) {
            this.swiper?.slideToLoop(index);
          } else {
            this.swiper?.slideTo(index);
          }
        });
      }

      /**
       * A centered row cannot be laid out before Swiper has measured it, and
       * loop mode inserts clones on top of that, so a centered carousel starts
       * hidden and fades in here rather than jumping into place.
       */
      reveal() {
        requestAnimationFrame(() => this.classList.remove('madara-carousel--hidden'));
      }
    }
  );
}
